import React, { useMemo, useState } from 'react';
import { ShieldCheck, Lock, Check, Download, Loader2, KeyRound, SearchX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, SeverityBadge, StatusBadge, ErrorState, LoadingState, EmptyState } from '@/components/primitives';
import { severityMeta } from '@/lib/severity';
import { domainOf } from './api';

const SOURCE_LABEL = { hibp: 'Public breach index', dehashed: 'DeHashed', local_breach_data: 'Local breach dataset' };

const csvCell = (v) => {
  const s = Array.isArray(v) ? v.join('; ') : String(v ?? '');
  // Neutralise spreadsheet formulas as well as quoting.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
};

/** Metadata only. Passwords are never part of an export. */
function exportCsv(rows) {
  const cols = ['email', 'breach_name', 'source', 'breach_date', 'severity', 'has_password', 'acknowledged', 'discovered_at', 'data_classes'];
  const body = [cols.join(','), ...rows.map((r) => cols.map((c) => csvCell(r[c])).join(','))].join('\r\n');
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `breach-results-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const byTriage = (a, b) =>
  severityMeta(b.severity).rank - severityMeta(a.severity).rank ||
  String(b.discovered_at || '').localeCompare(String(a.discovered_at || ''));
const byNewest = (a, b) => String(b.discovered_at || '').localeCompare(String(a.discovered_at || ''));

function ResultCard({ r, selected, onSelect, gate, onRetrieve, onAcknowledge, acking, onGoToDomains }) {
  const domain = domainOf(r.email);
  return (
    <article
      className="ws-result"
      data-selected={selected || undefined}
      data-testid={`breach-result-${r.id}`}
      aria-labelledby={`result-title-${r.id}`}
    >
      <div className="ws-result-check">
        {!r.acknowledged && (
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect(r.id, e.target.checked)}
            aria-label={`Select ${r.breach_name || 'breach'} for ${r.email}`}
          />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="ws-result-title">
          <SeverityBadge severity={r.severity} size="sm" />
          <h3 id={`result-title-${r.id}`} className="ws-result-name">{r.breach_name || 'Unnamed breach'}</h3>
          {r.has_password && <Badge tone="danger" size="sm" icon={Lock}>Password exposed</Badge>}
          <StatusBadge status={r.acknowledged ? 'acked' : 'new'} />
        </div>
        <div className="ws-result-meta">
          <span className="ws-mono ws-break" style={{ color: 'var(--text-primary)' }}>{r.email}</span>
          <span>{SOURCE_LABEL[r.source] || r.source}</span>
          {r.breach_date && <span>Breached {r.breach_date}</span>}
          {r.discovered_at && <span>Found {new Date(r.discovered_at).toLocaleDateString()}</span>}
        </div>
        {r.data_classes?.length > 0 && (
          <div className="ws-result-classes" aria-label="Exposed data">
            {r.data_classes.map((c) => <Badge key={c} size="sm" plain>{c}</Badge>)}
          </div>
        )}
        <div className="ws-result-actions">
          {r.has_password && (gate ? (
            <>
              <span className="ws-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Lock size={12} aria-hidden="true" />
                {gate === 'unverified'
                  ? `Verify ${domain} to reveal the exposed password.`
                  : `Add and verify ${domain} to reveal the exposed password.`}
              </span>
              <Button variant="outline" size="sm" onClick={onGoToDomains}>
                <ShieldCheck aria-hidden="true" /> {gate === 'unverified' ? 'Verify domain' : 'Add domain'}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => onRetrieve(r)}>
              <KeyRound aria-hidden="true" /> Retrieve password
            </Button>
          ))}
          {!r.acknowledged && (
            <Button variant="ghost" size="sm" onClick={() => onAcknowledge([r.id])} disabled={acking}>
              {acking ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Check aria-hidden="true" />} Acknowledge
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function BreachResultsList({ results, domains, onRetry, onRetrieve, onAcknowledge, onGoToDomains, onGoToEmails }) {
  const [q, setQ] = useState('');
  const [severity, setSeverity] = useState('all');
  const [status, setStatus] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [passwordsOnly, setPasswordsOnly] = useState(false);
  const [sort, setSort] = useState('triage');
  const [selected, setSelected] = useState(() => new Set());
  const [acking, setAcking] = useState(() => new Set());

  const all = useMemo(() => results.data?.results || [], [results.data]);
  const stats = results.data?.stats || {};
  const domainList = domains.data?.domains;

  // null while domains are unknown: then the server decides (and explains) on retrieve.
  const verified = useMemo(
    () => (domainList ? new Map(domainList.map((d) => [d.domain, Boolean(d.verified)])) : null),
    [domainList]
  );
  const gateFor = (email) => {
    if (!verified) return null;
    const d = domainOf(email);
    if (!verified.has(d)) return 'unmonitored';
    return verified.get(d) ? null : 'unverified';
  };

  const domainOptions = useMemo(() => [...new Set(all.map((r) => domainOf(r.email)).filter(Boolean))].sort(), [all]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all
      .filter((r) => severity === 'all' || severityMeta(r.severity).key === severity)
      .filter((r) => status === 'all' || (status === 'new' ? !r.acknowledged : r.acknowledged))
      .filter((r) => domainFilter === 'all' || domainOf(r.email) === domainFilter)
      .filter((r) => !passwordsOnly || r.has_password)
      .filter((r) => !needle || `${r.email} ${r.breach_name}`.toLowerCase().includes(needle))
      .sort(sort === 'triage' ? byTriage : byNewest);
  }, [all, q, severity, status, domainFilter, passwordsOnly, sort]);

  if (results.loading && !results.data) return <LoadingState label="breach results" rows={4} />;
  if (results.error && !results.data) {
    return <ErrorState title="Couldn't load breach results" error={results.error} onRetry={onRetry} />;
  }

  if (all.length === 0) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="No breach results"
        description="None of the checked addresses has appeared in a breach source so far. Addresses marked “Not checked yet” on the Emails tab have no result either way."
        action={<Button variant="outline" onClick={onGoToEmails}>Review emails</Button>}
      />
    );
  }

  const selectedVisible = visible.filter((r) => selected.has(r.id) && !r.acknowledged);
  const filtered = q || severity !== 'all' || status !== 'all' || domainFilter !== 'all' || passwordsOnly;

  const toggle = (id, on) => setSelected((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const ack = async (ids) => {
    setAcking((prev) => new Set([...prev, ...ids]));
    try {
      await onAcknowledge(ids);
      setSelected((prev) => new Set([...prev].filter((id) => !ids.includes(id))));
    } finally {
      setAcking((prev) => new Set([...prev].filter((id) => !ids.includes(id))));
    }
  };

  return (
    <div className="ws-stack">
      {results.error && (
        <ErrorState variant="inline" title="Showing the last loaded results" error={results.error} onRetry={onRetry} />
      )}

      <div className="ws-filterbar" role="search" aria-label="Filter breach results">
        <div className="ws-field ws-field--grow">
          <label className="ws-label" htmlFor="br-search">Search</label>
          <Input id="br-search" className="ws-input-inset" placeholder="Email or breach name" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="ws-field">
          <label className="ws-label" htmlFor="br-sev">Severity</label>
          <select id="br-sev" className="ws-select" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="all">All severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div className="ws-field">
          <label className="ws-label" htmlFor="br-status">Status</label>
          <select id="br-status" className="ws-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            <option value="new">New (unacknowledged)</option>
            <option value="acked">Acknowledged</option>
          </select>
        </div>
        {domainOptions.length > 1 && (
          <div className="ws-field">
            <label className="ws-label" htmlFor="br-domain">Domain</label>
            <select id="br-domain" className="ws-select" value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)}>
              <option value="all">All domains</option>
              {domainOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        )}
        <div className="ws-field">
          <label className="ws-label" htmlFor="br-sort">Sort</label>
          <select id="br-sort" className="ws-select" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="triage">Severity, then newest</option>
            <option value="newest">Newest first</option>
          </select>
        </div>
        <label className="ws-check" style={{ alignSelf: 'center', paddingTop: 18 }}>
          <input type="checkbox" checked={passwordsOnly} onChange={(e) => setPasswordsOnly(e.target.checked)} />
          <span>Password exposed only</span>
        </label>
      </div>

      <div className="ws-toolbar">
        <span className="ws-meta" role="status" aria-live="polite">
          {filtered ? `${visible.length} of ${all.length} results match` : `${all.length} results`}
          {stats.total > all.length && ` · showing the ${all.length} most recent of ${stats.total}; filters apply to these only`}
        </span>
        <span className="ws-toolbar-spacer" />
        <Button variant="outline" size="sm" onClick={() => exportCsv(visible)} disabled={!visible.length}>
          <Download aria-hidden="true" /> Export CSV
        </Button>
      </div>

      {selectedVisible.length > 0 && (
        <div className="ws-bulkbar" role="region" aria-label="Bulk actions">
          <span>{selectedVisible.length} selected</span>
          <Button size="sm" onClick={() => ack(selectedVisible.map((r) => r.id))} disabled={acking.size > 0}>
            {acking.size > 0 ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Check aria-hidden="true" />}
            Acknowledge selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear selection</Button>
        </div>
      )}
      {selectedVisible.length === 0 && visible.some((r) => !r.acknowledged) && (
        <div>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set(visible.filter((r) => !r.acknowledged).map((r) => r.id)))}>
            Select all unacknowledged ({visible.filter((r) => !r.acknowledged).length})
          </Button>
        </div>
      )}

      {visible.length === 0 ? (
        <EmptyState
          icon={SearchX}
          variant="compact"
          title="No results match these filters"
          action={<Button variant="outline" size="sm" onClick={() => { setQ(''); setSeverity('all'); setStatus('all'); setDomainFilter('all'); setPasswordsOnly(false); }}>Clear filters</Button>}
        />
      ) : (
        <div className="ws-stack" style={{ gap: 'var(--space-2)' }}>
          {visible.map((r) => (
            <ResultCard
              key={r.id}
              r={r}
              selected={selected.has(r.id)}
              onSelect={toggle}
              gate={r.has_password ? gateFor(r.email) : null}
              onRetrieve={onRetrieve}
              onAcknowledge={ack}
              acking={acking.has(r.id)}
              onGoToDomains={onGoToDomains}
            />
          ))}
        </div>
      )}
    </div>
  );
}
